import {useShallowMemo, useWritable} from '@raison/ui-kit';
import {Action} from 'history';
import React, {Fragment, useEffect, useMemo, useRef} from 'react';
import {
    Location,
    UNSAFE_LocationContext as LocationContext,
    matchRoutes,
    renderMatches,
    useLocation,
    useNavigate,
    useNavigationType,
} from 'react-router';
import {DefaultGate, GateComponent, GateStep} from './GateComponent';

export function GatedSwitch({fallback, routes}: {fallback: React.ReactNode; routes: GatedRoute[]}) {
    const location = useLocation();
    const action = useNavigationType();

    // finding and rendering routes, separated into it's own memo
    // to avoid recomputing on location.search or location.state changes
    const match = useMemo(() => {
        const [raw] = matchRoutes(routes, location.pathname) ?? [null];
        return (
            raw && {
                Gate: raw.route.handle.Gate ?? DefaultGate,
                pattern: raw.route.path,
                prerender: renderMatches([raw]),
            }
        );
    }, [routes, location.pathname]);

    const matchedPage: GatedPage | null = useMemo(() => {
        return (
            match && {
                key: match.pattern,
                Gate: match.Gate,
                render: () => match.prerender,
                location: location,
            }
        );
    }, [match, location]);

    const visitedPageRef = useRef<GatedPage | null>(null);
    const [tx, setTx] = useWritable(() => {
        const visitedPage = visitedPageRef.current;
        /**
         * It's unclear if skipping Gate activation on traversal is entirely correct.
         * Pros:
         * + user already visited the page, no reason to check
         * + we would have to pass `step` prop, but treating
         *   navigation back to the page as GateStep.Entry is misleading
         * Cons:
         * - conditions for passing the gate might have changed
         * - since gate opens AFTER navigation page can be considered visited even
         *   if the gate didn't pass and instead rendered a link to another page
         *   or redirected with a PUSH instead of REPLACE; in such cases navigation back
         *   will bypass the gate. However, gates can be bypassed anyway - with
         *   direction navigation through address line and should handle this anyway.
         */
        const isTraversal = action === Action.Pop;
        const isAllowed =
            isTraversal ||
            !matchedPage || // No reason to do anything with gates when there's no match
            !visitedPage || // or no previous page.
            matchedPage.Gate === DefaultGate || // DefaultGate always passes.
            matchedPage.Gate === visitedPage.Gate || // Gate was visited, which means it was passed
            matchedPage.key === visitedPage.key; // Gate lead user back.

        if (isAllowed) {
            return {isAllowed, visited: matchedPage};
        } else {
            return {isAllowed, exiting: visitedPage, entering: matchedPage};
        }
    }, [matchedPage]);

    // The visitedPageRef should be updated only if tx update
    // was neither suspended nor discarded (due to secondary update called somewhere in render).
    // The <Navigate/> updates location in useEffect, which is not counted as
    // secondary update. This total situation creates a case where pages that
    // had <Navigate/> rendered still briefly remembered as visited.
    // In practice it hasn't yet proven to be a problem since the page they redirect
    // towards is almost immediately set as visited. But still the logic is flawed
    // and should be fixed if opportunity presents itself.
    useEffect(() => {
        if (tx.isAllowed) {
            visitedPageRef.current = tx.visited;
        }
    }, [tx]);

    const navigate = useNavigate();
    const rollbackTx = () => {
        const visitedPage = visitedPageRef.current;
        if (visitedPage === null) {
            return;
        }
        if (action === Action.Replace) {
            navigate(visitedPage.location, {replace: true});
        }
        if (action === Action.Push) {
            navigate(-1);
        }
    };

    const commitTx = () => {
        if (tx.isAllowed) {
            return;
        }
        setTx({isAllowed: true, visited: tx.entering});
    };

    let thisPage: GatedPage | null = matchedPage;
    let nextPage: GatedPage | null = null;
    if (!tx.isAllowed) {
        nextPage = tx.entering;
        thisPage = tx.exiting;
    }

    return (
        <Fragment>
            {thisPage === null ? (
                fallback
            ) : (
                <GateLocationProvider key={thisPage.key} page={thisPage}>
                    <thisPage.Gate //
                        step={nextPage == null ? GateStep.Idle : GateStep.Exit}
                        deny={warnAboutDenyingAfterEntry}
                        pass={thisPage.render}
                    />
                </GateLocationProvider>
            )}
            {nextPage !== null && (
                <GateLocationProvider key={nextPage.key} page={nextPage}>
                    <nextPage.Gate //
                        step={GateStep.Entry}
                        deny={rollbackTx}
                        pass={() => <Effect onMount={commitTx} />}
                    />
                </GateLocationProvider>
            )}
        </Fragment>
    );
}

type GatedPage = {
    readonly key: React.Key;
    readonly location: Location;
    readonly Gate: GateComponent;
    readonly render: () => React.ReactNode;
};

type GatedRoute = {
    handle: {
        Gate?: GateComponent | undefined;
    };
    path: string;
    element: React.JSX.Element;
};

function GateLocationProvider({page, children}: React.PropsWithChildren<{page: GatedPage}>) {
    const navigationType = useNavigationType();
    return (
        <LocationContext.Provider value={useShallowMemo({location: page.location, navigationType})}>
            {children}
        </LocationContext.Provider>
    );
}

function Effect({onMount: effect, children}: React.PropsWithChildren<{onMount: () => void}>) {
    useEffect(effect, []);
    return children;
}

/**
 * Unclear what denying should do if it's called on already matched route.
 * If we had indices on history entries, we could save the index where route
 * was matched and call `go(routeMatchIndex - currentIndex - 1)` to go back
 * to the route that was before.
 */
function warnAboutDenyingAfterEntry() {
    const message = 'Cannot deny entry, Gate already passed';
    if (import.meta.env.DEV) {
        throw new Error(message);
    } else {
        console.warn(message);
    }
}
